/**
 * Jixxeo Coin Toss Live — best of 5. Both players call H/T each round.
 * Server picks a coin outcome by RNG (from one side) — we implement:
 * Player who called correctly earns a point. First to 3 wins.
 * To keep it fair, the "coin" is derived from a shared seed = round hash.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MpShell } from "./_mpShell";
import { useMpClient } from "./_mpClient";

const TARGET = 3;

function coinFlip(seed) {
  // deterministic-ish from seed
  const n = String(seed).split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return (n * 9301 + 49297) % 2 === 0 ? "H" : "T";
}

export default function JixxeoCoinToss() {
  const loc = useLocation();
  const challenge = new URLSearchParams(loc.search).get("challenge");
  const [stake, setStake] = useState(0);
  const [started, setStarted] = useState(!!challenge);
  const [key, setKey] = useState(0);

  const [round, setRound] = useState(1);
  const [myCall, setMyCall] = useState(null);
  const [oppCall, setOppCall] = useState(null);
  const [coin, setCoin] = useState(null);
  const [myScore, setMyScore] = useState(0);
  const [oppScore, setOppScore] = useState(0);
  const [history, setHistory] = useState([]);

  const client = useMpClient({ game: "coin-toss", stake, challenge, autoConnect: started });

  const roomSeed = client.room || "solo";

  useEffect(() => {
    setRound(1); setMyCall(null); setOppCall(null); setCoin(null);
    setMyScore(0); setOppScore(0); setHistory([]);
  }, [key]);

  useEffect(() => {
    if (!client.lastMove) return;
    const call = client.lastMove?.move?.call;
    if (call === "H" || call === "T") setOppCall(call);
  }, [client.lastMove]);

  // When both called, flip
  useEffect(() => {
    if (myCall && oppCall && !coin) {
      const c = coinFlip(`${roomSeed}-${round}`);
      setCoin(c);
      const myWin = myCall === c && oppCall !== c;
      const oppWin = oppCall === c && myCall !== c;
      const tie = myCall === oppCall;
      let winner = "tie";
      if (myWin) { setMyScore((s) => s + 1); winner = "me"; }
      if (oppWin) { setOppScore((s) => s + 1); winner = "opp"; }
      setHistory((h) => [...h, { round, my: myCall, opp: oppCall, coin: c, winner }]);
      setTimeout(() => {
        if (myScore + (myWin ? 1 : 0) >= TARGET || oppScore + (oppWin ? 1 : 0) >= TARGET) {
          // match end handled below via effect
          return;
        }
        setMyCall(null); setOppCall(null); setCoin(null);
        setRound((r) => r + 1);
      }, 1600);
    }
  }, [myCall, oppCall]); // eslint-disable-line

  useEffect(() => {
    if (myScore >= TARGET) client.sendResult("win");
    else if (oppScore >= TARGET) client.sendResult("lose");
  }, [myScore, oppScore]); // eslint-disable-line

  const call = (c) => {
    if (myCall) return;
    if (client.status !== "paired") return;
    if (myScore >= TARGET || oppScore >= TARGET) return;
    setMyCall(c);
    client.sendMove({ call: c });
  };

  const isDone = myScore >= TARGET || oppScore >= TARGET;

  return (
    <MpShell title="Jixxeo Coin Toss" accent="#8B5CF6" game="coin-toss" gameSlug="jixxeo-coin-toss"
      status={started ? client.status : "idle"}
      self={client.self} opponent={client.opponent}
      settlement={client.settlement} stake={stake} setStake={setStake}
      onFindOpponent={() => { setStarted(true); setTimeout(() => client.connect(), 0); }}
      onRematch={() => { client.close(); setKey((k) => k + 1); setStarted(false); setTimeout(() => { setStarted(true); client.connect(); }, 100); }}
      initialChallenge={challenge}>
      <div data-testid="coin-body" className="space-y-4">
        <div className="flex items-center justify-around">
          <div className="text-center">
            <div className="mono text-[9px] uppercase text-[#00F0FF]">// you</div>
            <div className="font-heading font-black text-3xl">{myScore}</div>
          </div>
          <div className="text-center">
            <div className="mono text-[9px] uppercase text-white/60">round</div>
            <div className="font-heading font-black text-2xl text-[#FFE300]">{round}/5</div>
          </div>
          <div className="text-center">
            <div className="mono text-[9px] uppercase text-[#FF003C]">// opp</div>
            <div className="font-heading font-black text-3xl">{oppScore}</div>
          </div>
        </div>

        <div className="aspect-square max-w-[220px] mx-auto rounded-full flex items-center justify-center border-4 border-[#8B5CF6]/40 bg-[#8B5CF6]/10 relative">
          <div className={`text-6xl font-black font-heading text-[#FFE300] ${coin ? "" : "opacity-40"}`}
            style={{ transform: coin ? "rotate(0deg)" : "rotate(0deg)" }}>
            {coin ? coin : "?"}
          </div>
        </div>

        {!isDone && !myCall && (
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="coin-call-H" onClick={() => call("H")}
              disabled={client.status !== "paired"}
              className="py-4 rounded-2xl border-2 border-[#00F0FF] bg-[#00F0FF]/10 hover:bg-[#00F0FF]/20 font-heading font-black text-2xl">
              HEADS
            </button>
            <button data-testid="coin-call-T" onClick={() => call("T")}
              disabled={client.status !== "paired"}
              className="py-4 rounded-2xl border-2 border-[#FF003C] bg-[#FF003C]/10 hover:bg-[#FF003C]/20 font-heading font-black text-2xl">
              TAILS
            </button>
          </div>
        )}

        {myCall && !coin && (
          <div className="mono text-[10px] uppercase tracking-widest text-center text-white/50">
            You called {myCall} · {oppCall ? "Flipping…" : "Waiting for opponent…"}
          </div>
        )}

        {history.length > 0 && (
          <div className="flex justify-center gap-1">
            {history.map((r) => (
              <span key={r.round} className={`w-3 h-3 rounded-full ${r.winner === "me" ? "bg-[#39FF14]" : r.winner === "opp" ? "bg-[#FF003C]" : "bg-white/40"}`} />
            ))}
          </div>
        )}
      </div>
    </MpShell>
  );
}
